import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Editable,
  EditableInput,
  EditablePreview,
  Flex,
  Icon,
  IconButton,
  Image,
  Link,
  Tooltip,
  useBreakpointValue,
} from "@chakra-ui/react";
import { BiImages, BiUpload, BiX } from "react-icons/bi";
import useScraper from "./useScraper";
import useDebounce from "../../../_hooks/useDebounce";
import { getUrl } from "../../../_utils/url";
import { trackBasicEvent } from "../../../_utils/eventsTracker";
import PreloaderWrapper from "../Preloader/PreloaderWrapper";
import Preloader from "../Preloader/Preloader";
import { translate as trans } from "../Trans/Trans";
import PostLinkPreviewEditLinkPopover from "./PostLinkPreviewEditLinkPopover";
import PostLinkImageSelectorModal from "./PostLinkImageSelectorModal";

const toolbarStyles = {
  position: "absolute",
  right: "2",
  top: "2",
};

const toolbarButtonStyles = {
  height: "",
  minWidth: "",
  padding: "1",
  marginLeft: "1",
  boxShadow: "base",
  borderRadius: "full",
  backgroundColor: "gray.500",
  color: "white",
  _hover: {
    backgroundColor: "black",
  },
};

const PostLinkPreview = ({
  link,
  title,
  description,
  image,
  loading,
  autoFetch,
  onRemove,
  onLinkChange,
  onTitleChange,
  onDescriptionChange,
  onImageAdd,
  onImageChange,
}) => {
  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const { fetchScraperData } = useScraper();
  const [imageHover, setImageHover] = useState(false);
  const [imageSelectorActive, setImageSelectorActive] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const [data, setData] = useState({
    title,
    description,
    image,
  });
  const debouncedTitle = useDebounce(data.title, 100);
  const debouncedDescription = useDebounce(data.description, 100);
  const url = getUrl(link);

  const fetchLinkData = useCallback(
    async (fetchLink) => {
      setInProgress(true);

      const fetchedData = await fetchScraperData(fetchLink);

      setData({
        title: fetchedData?.title || "",
        description: fetchedData?.description || "",
        image: fetchedData?.images?.[0] || "",
      });

      setInProgress(false);
    },
    [setData, setInProgress, fetchScraperData]
  );

  const handleImageMouseEnter = useCallback(
    () => setImageHover(true),
    [setImageHover]
  );

  const handleImageMouseLeave = useCallback(
    () => setImageHover(false),
    [setImageHover]
  );

  const handleRemove = useCallback(() => {
    onRemove?.();

    trackBasicEvent("post-link-preview", "click", "remove");
  }, [onRemove]);

  const handleLinkChange = useCallback(
    (value) => {
      onLinkChange?.(value);

      trackBasicEvent("post-link-preview", "click", "link change");
    },
    [onLinkChange]
  );

  const handleTitleChange = useCallback(
    (value) => {
      setData({
        ...data,
        title: value,
      });
    },
    [data, setData]
  );

  const handleTitleBlur = useCallback(() => {
    trackBasicEvent("post-link-preview", "click", "title change");
  }, []);

  const handleDescriptionChange = useCallback(
    (value) => {
      setData({
        ...data,
        description: value,
      });
    },
    [data, setData]
  );

  const handleDescriptionBlur = useCallback(() => {
    trackBasicEvent("post-link-preview", "click", "description change");
  }, []);

  const handleImageAdd = useCallback(() => {
    onImageAdd?.();

    trackBasicEvent("post-link-preview", "click", "image add");
  }, [onImageAdd]);

  const handleImageChange = useCallback(
    (value) => {
      onImageChange?.(value);

      trackBasicEvent("post-link-preview", "click", "image change");
    },
    [onImageChange]
  );

  const handleImageSelectorOpen = useCallback(
    () => setImageSelectorActive(true),
    [setImageSelectorActive]
  );

  const handleImageSelectorClose = useCallback(
    () => setImageSelectorActive(false),
    [setImageSelectorActive]
  );

  useEffect(() => {
    setData({ title, description, image });
  }, [title, description, image]);

  useEffect(() => {
    onTitleChange?.(data.title);
  }, [debouncedTitle]);

  useEffect(() => {
    onDescriptionChange?.(data.description);
  }, [debouncedDescription]);

  useEffect(() => {
    if (autoFetch && link) {
      fetchLinkData(link);
    }
  }, [link, autoFetch]);

  return (
    <Flex
      position="relative"
      alignItems="flex-start"
      backgroundColor="gray.800"
      borderRadius="sm"
      color="white"
    >
      <Box
        padding="2"
        width="10rem"
        height="8rem"
        position="relative"
        onMouseEnter={
          onImageAdd || onImageChange ? handleImageMouseEnter : null
        }
        onMouseLeave={
          onImageAdd || onImageChange ? handleImageMouseLeave : null
        }
      >
        {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
        <Image
          src={data.image}
          fallback={<></>}
          width="100%"
          height="100%"
          objectFit="contain"
        />
        {(isSmallScreen || imageHover) && (onImageAdd || onImageChange) ? (
          <Box sx={toolbarStyles}>
            {onImageAdd ? (
              <Tooltip
                label={trans("post_link_preview.image.upload")}
                hasArrow
                placement="top"
              >
                <IconButton
                  sx={toolbarButtonStyles}
                  icon={<Icon as={BiUpload} fontSize="lg" />}
                  onClick={handleImageAdd}
                />
              </Tooltip>
            ) : null}
            {onImageChange ? (
              <Tooltip
                label={trans("post_link_preview.image.select")}
                hasArrow
                placement="top"
              >
                <IconButton
                  sx={toolbarButtonStyles}
                  icon={<Icon as={BiImages} fontSize="lg" />}
                  onClick={handleImageSelectorOpen}
                />
              </Tooltip>
            ) : null}
          </Box>
        ) : null}
      </Box>
      <Box padding="2" paddingLeft="0" width="calc(100% - 10rem)">
        <Editable
          value={data.title}
          selectAllOnFocus={false}
          isPreviewFocusable={!!onTitleChange}
          isDisabled={!onTitleChange}
          onChange={onTitleChange ? handleTitleChange : null}
          onBlur={onTitleChange ? handleTitleBlur : null}
          placeholder={
            onTitleChange ? trans("post_link_preview.title.placeholder") : null
          }
          marginRight="5"
          fontSize="sm"
          lineHeight="1.2em"
          color="inherit"
        >
          <Tooltip
            label={trans("post_link_preview.title.click_to_change")}
            hasArrow
            placement="top"
            isDisabled={!onTitleChange}
            shouldWrapChildren
          >
            <EditablePreview
              display="block"
              cursor={onTitleChange ? "pointer" : null}
              _after={{
                content: '"."',
                visibility: "hidden",
              }}
            />
          </Tooltip>
          <EditableInput />
        </Editable>
        <Editable
          value={data.description}
          selectAllOnFocus={false}
          isPreviewFocusable={!!onDescriptionChange}
          isDisabled={!onDescriptionChange}
          onChange={onDescriptionChange ? handleDescriptionChange : null}
          onBlur={onDescriptionChange ? handleDescriptionBlur : null}
          placeholder={
            onDescriptionChange
              ? trans("post_link_preview.description.placeholder")
              : null
          }
          fontSize="xs"
          lineHeight="1.2em"
          color="inherit"
        >
          <Tooltip
            label={trans("post_link_preview.description.click_to_change")}
            hasArrow
            placement="top"
            isDisabled={!onDescriptionChange}
            shouldWrapChildren
          >
            <EditablePreview
              display="block"
              cursor={onDescriptionChange ? "pointer" : null}
              _after={{
                content: '"."',
                visibility: "hidden",
              }}
            />
          </Tooltip>
          <EditableInput />
        </Editable>
        <Box display="flex" alignItems="center" marginTop="1" marginBottom="1">
          <Box fontSize="sm">
            <Link href={link} title={link} isExternal>
              {url?.hostname || ""}
            </Link>
          </Box>
          {onLinkChange ? (
            <Box marginLeft="1">
              <PostLinkPreviewEditLinkPopover
                value={link}
                onSave={handleLinkChange}
              />
            </Box>
          ) : null}
        </Box>
      </Box>
      {loading || inProgress ? (
        <PreloaderWrapper padding="2">
          <Preloader />
        </PreloaderWrapper>
      ) : null}
      {onRemove ? (
        <Tooltip
          label={trans("post_link_preview.remove")}
          hasArrow
          placement="top"
        >
          <IconButton
            position="absolute"
            zIndex="3"
            top="1"
            right="1"
            variant="unstyled"
            size="xs"
            icon={<Icon as={BiX} fontSize="sm" />}
            onClick={handleRemove}
          />
        </Tooltip>
      ) : null}
      {onImageChange && imageSelectorActive ? (
        <PostLinkImageSelectorModal
          link={link}
          onSelect={handleImageChange}
          onClose={handleImageSelectorClose}
        />
      ) : null}
    </Flex>
  );
};

PostLinkPreview.propTypes = {
  link: PropTypes.string.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  loading: PropTypes.bool,
  autoFetch: PropTypes.bool,
  onRemove: PropTypes.func,
  onLinkChange: PropTypes.func,
  onTitleChange: PropTypes.func,
  onDescriptionChange: PropTypes.func,
  onImageAdd: PropTypes.func,
  onImageChange: PropTypes.func,
};

PostLinkPreview.defaultProps = {
  title: undefined,
  description: undefined,
  image: undefined,
  loading: undefined,
  autoFetch: undefined,
  onRemove: undefined,
  onLinkChange: undefined,
  onTitleChange: undefined,
  onDescriptionChange: undefined,
  onImageAdd: undefined,
  onImageChange: undefined,
};

export default PostLinkPreview;
